import { isImage, isVideo } from 'utilities/file';

import SingleProducerMeeting from './single-producer-meeting';
import EventsInterfaces from './events';
import SocialPostActions from './social-post-actions';

class Community {

  constructor(basePath, communityId, apiClient) {

    this.apiClient = apiClient;
    this.basePath = `${basePath}/communities/${communityId}`;
    this.postBasePath = `${this.basePath}/posts/:postId`;

    this.singleProducerMeeting = new SingleProducerMeeting(this.postBasePath, apiClient);
    this.events = new EventsInterfaces(this.postBasePath, apiClient);
    this.socialPostActions = new SocialPostActions(this.basePath, apiClient);

  }

  async updateSearchEngineSettingsById(payload) {

    const path = this.basePath + '/seo-settings';

    return this.apiClient.put(path, {}, {}, payload);

  }

  async uploadSearchEngineImageSettingsById(file) {

    const path = this.basePath + '/search-engine-settings-image';

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, {}, {}, data, true);

  }

  async updateGeneralAndPostSettingsById(payload) {

    const path = this.basePath + '/general-and-post-settings';

    return this.apiClient.put(path, {}, {}, payload);

  }

  async bootstrap(query, urlParameters) {

    const path = this.basePath + '/bootstrap';

    return this.apiClient.post(path, {}, {}, { query, urlParameters });

  }

  async getTrendingTagsByCommunityId(paginationOptions) {

    const path = this.basePath + '/trending-tags';

    return this.apiClient.get(path, {}, { paginationOptions });

  }

  /**
   * 
   * Example: updateOrCreateTag({ name: 'sample-tag'});
   * 
   * */
  async updateOrCreateTag(tag) {

    const path = this.basePath + '/tags';

    return this.apiClient.post(path, {}, {}, tag);

  }

  /**
   * 
   * Example: updateTagById(`${tag._id}`, { name: 'sample-tag'});
   * 
   * */
  async updateTagById(tagId, tag) {

    const path = this.basePath + '/tags/:tagId';

    return this.apiClient.put(path, { tagId }, {}, tag);

  }

  async deleteTag(tag) {

    const path = this.basePath + '/tags/:name';

    return this.apiClient.delete(path, { name: tag });

  }

  /**
   * 
   * const tags = await getTagsByCommunityId();
   * // tags => [{ name: 'sample-tag'}]
   * 
   * **/
  async getTagsByCommunityId() {

    const path = this.basePath + '/tags';

    return this.apiClient.get(path, {}, {});

  }

  async getPaginatedPosts(filterOptions, sortOptions, paginationOptions) {

    const path = this.basePath + '/posts';

    return this.apiClient.get(path, {}, {
      filterOptions,
      sortOptions,
      paginationOptions
    });

  }

  async getScheduledPostsByQueryOptions(queryOptions) {

    const path = this.basePath + '/scheduled-posts';

    return this.apiClient.get(path, {}, queryOptions);

  }

  async createPostDraft() {

    const path = this.basePath + '/posts';

    return this.apiClient.post(path, {}, {}, {});

  }

  async updatePostTitleById(postId, title) {

    const path = this.basePath + '/posts/:postId/title';

    return this.apiClient.put(path, { postId }, {}, { title });

  }

  async updatePostYoutubeTitleById(postId, title) {

    const path = this.basePath + '/posts/:postId/youtube-title';

    return this.apiClient.put(path, { postId }, {}, { title });

  }

  async updatePostTiktokTitleById(postId, title) {

    const path = this.basePath + '/posts/:postId/tiktok-title';

    return this.apiClient.put(path, { postId }, {}, { title });

  }

  async updatePostRedditTitleById(postId, title) {

    const path = this.basePath + '/posts/:postId/reddit-title';

    return this.apiClient.put(path, { postId }, {}, { title });

  }

  async updatePostRedditCommunityById(postId, community) {

    const path = this.basePath + '/posts/:postId/reddit-community';

    return this.apiClient.put(path, { postId }, {}, { community });

  }

  async updatePostRedditFlairById(postId, flairId, flairText) {

    const path = this.basePath + '/posts/:postId/reddit-flair';

    return this.apiClient.put(path, { postId }, {}, { flairId, flairText });

  }

  async updatePostDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/draft-content';

    return this.apiClient.put(path, { postId }, {}, { draftContent });

  }

  async updatePostFacebookDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/facebook-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostInstagramDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/instagram-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostTwitterDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/twitter-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostYoutubeDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/youtube-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostTiktokDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/tiktok-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostRedditDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/reddit-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  async updatePostLinkedInDraftContentById(postId, draftContent) {

    const path = this.basePath + '/posts/:postId/linked-in-draft-content';

    return this.apiClient.put(path, { postId }, {}, draftContent);

  }

  /**
   * const postId = "...";
   * const tags = ["tag-one", "tag-two"];
   * 
   * await updatePostTagsById(postId, tags);
   * 
   * **/
  async updatePostTagsById(postId, tags) {

    const path = this.basePath + '/posts/:postId/tags';

    return this.apiClient.put(path, { postId }, {}, tags);

  }

  async updatePostSettingsById(postId, settings) {

    const path = this.basePath + '/posts/:postId/settings';

    return this.apiClient.put(path, { postId }, {}, settings);

  }

  async updateScheduledPostSettingsById(postId, settings) {

    const path = this.basePath + '/scheduled-posts/:postId/settings';

    return this.apiClient.put(path, { postId }, {}, settings);

  }

  async completePostWithSettingsById(postId, settings) {

    const path = this.basePath + '/posts/:postId/complete';

    return this.apiClient.put(path, { postId }, {}, settings);

  }

  async updatePostById(postId, payload) {

    const path = this.basePath + '/posts/:postId/update';

    return this.apiClient.put(path, { postId }, {}, payload);

  }

  async deleteScheduledPost(postId) {

    const path = this.basePath + '/posts/:postId/scheduled';

    return this.apiClient.delete(path, { postId }, {});

  }

  async uploadPostFile(postId, file) {

    let path = this.basePath + '/posts/:postId';

    if (isImage(file)) {
      path = `${path}/images`;
    } else if (isVideo(file)) {
      path = `${path}/videos`;
    } else {
      path = `${path}/images`;
    }

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { postId }, {}, data, true);

  }

  async getScheduledPostById(postId, projection) {

    const path = this.basePath + '/posts/:postId/scheduled';

    return this.apiClient.get(path, { postId }, { projection });

  }

  async getFullPostById(postId) {

    const path = this.basePath + '/posts/:postId/full';

    return this.apiClient.get(path, { postId });

  }

  async getFullPostByHumanFriendlyId(humanFriendlyId) {

    const path = this.basePath + '/posts/:humanFriendlyId/human-friendly';

    return this.apiClient.get(path, { humanFriendlyId });

  }

  async createReaction(postId, reaction) {

    const path = this.basePath + '/posts/:postId/reactions';

    return this.apiClient.post(path, { postId }, {}, reaction);

  }

  async deleteReactionById(postId, reactionId) {

    const path = this.basePath + '/posts/:postId/reactions/:reactionId';

    return this.apiClient.delete(path, { postId, reactionId }, {});

  }

  async getReactionsByReceiverIdAndType(postId, type, projection) {

    const path = this.basePath + '/posts/:postId/reactions/:type';

    return this.apiClient.get(path, { postId, type }, { projection });

  }

  async getCommentReactionsByReceiverIdAndType(postId, commentId, type, projection) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/reactions/:type';

    return this.apiClient.get(path, { postId, commentId, type }, { projection });

  }

  async getReplyReactionsByReceiverIdAndType(postId, commentId, replyId, type, projection) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/reactions/:type';

    return this.apiClient.get(path, { postId, commentId, replyId, type }, { projection });

  }

  async deletePostById(postId) {

    const path = this.basePath + '/posts/:postId';

    return this.apiClient.delete(path, { postId }, {});

  }

  async pinPostOnCommunity(postId) {

    const path = this.basePath + '/posts/:postId/pin';

    return this.apiClient.put(path, { postId }, {}, {});

  }

  async tryUnsetPinnedPostById(postId) {

    const path = this.basePath + '/posts/:postId/pin';

    return this.apiClient.delete(path, { postId }, {}, {});

  }

  async updateCommentsEnabledById(postId, payload) {

    const path = this.basePath + '/posts/:postId/comments-enabled';

    return this.apiClient.put(path, { postId }, {}, payload);

  }

  async updatePostReactionsEnabledById(postId, payload) {

    const path = this.basePath + '/posts/:postId/reactions-enabled';

    return this.apiClient.put(path, { postId }, {}, payload);

  }

  async getCommentSuggestions(postId) {

    const path = this.basePath + '/posts/:postId/comment-suggestions';

    return this.apiClient.get(path, { postId }, {});

  }

  async getReplySuggestions(postId, commentId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/reply-suggestions';

    return this.apiClient.get(path, { postId, commentId }, {});

  }

  async createCommentDraft(postId) {

    const path = this.basePath + '/posts/:postId/comments';

    return this.apiClient.post(path, { postId }, {}, {});

  }

  async deleteCommentById(postId, commentId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId';

    return this.apiClient.delete(path, { postId, commentId }, {});

  }

  async deleteReplyById(postId, commentId, replyId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId';

    return this.apiClient.delete(path, { postId, commentId, replyId }, {});

  }

  async publishCommentById(postId, commentId, content) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/publish';

    return this.apiClient.put(path, { postId, commentId }, {}, { content });

  }

  async getCommentContentById(postId, commentId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/content';

    return this.apiClient.get(path, { postId, commentId }, {});

  }

  async updateCommentById(postId, commentId, content) {

    const path = this.basePath + '/posts/:postId/comments/:commentId';

    return this.apiClient.put(path, { postId, commentId }, {}, { content });

  }

  async createCommentReaction(postId, commentId, payload) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/reactions';

    return this.apiClient.post(path, { postId, commentId }, {}, payload);

  }

  async deleteCommentReactionById(postId, commentId, reactionId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/reactions/:reactionId';

    return this.apiClient.delete(path, { postId, commentId, reactionId }, {});

  }

  async createReplyDraft(postId, commentId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies';

    return this.apiClient.post(path, { postId, commentId }, {}, {});

  }

  async publishReplyById(postId, commentId, replyId, content) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/publish';

    return this.apiClient.put(path, { postId, commentId, replyId }, {}, { content });

  }

  async getReplyContentById(postId, commentId, replyId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/content';

    return this.apiClient.get(path, { postId, commentId, replyId }, {});

  }

  async updateReplyById(postId, commentId, replyId, content) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId';

    return this.apiClient.put(path, { postId, commentId, replyId }, {}, { content });

  }

  async createReplyReaction(postId, commentId, replyId, payload) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/reactions';

    return this.apiClient.post(path, { postId, commentId, replyId }, {}, payload);

  }

  async deleteReplyReactionById(postId, commentId, replyId, reactionId) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/reactions/:reactionId';

    return this.apiClient.delete(path, { postId, commentId, replyId, reactionId });

  }

  async uploadCommentImage(postId, commentId, file) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/images';

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { postId, commentId }, {}, data, true);

  }

  async uploadReplyImage(postId, commentId, replyId, file) {

    const path = this.basePath + '/posts/:postId/comments/:commentId/replies/:replyId/images';

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { postId, commentId, replyId }, {}, data, true);

  }

  async uploadOpenGraphImage(postId, file) {

    const path = this.basePath + '/posts/:postId/search-engine-settings-image';

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { postId }, {}, data, true);

  }

  async createPoll(postId) {

    const path = this.basePath + '/posts/:postId/poll';

    return this.apiClient.post(path, { postId }, {}, {});

  }

  async updateAndPublishPollById(postId, pollId, update) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/publish';

    return this.apiClient.put(path, { postId, pollId }, {}, update);

  }

  /**
   *
   * Example:
   * updatePollById(postId, pollId, {endsAt: Date.now()});
   *  
   **/
  async updatePollById(postId, pollId, update) {

    const path = this.basePath + '/posts/:postId/poll/:pollId';

    return this.apiClient.put(path, { postId, pollId }, {}, update);

  }

  async getPopulatedPollById(postId, pollId) {

    const path = this.basePath + '/posts/:postId/poll/:pollId';

    return this.apiClient.get(path, { postId, pollId }, {}, {});

  }

  async getPopulatedPollByPostId(postId) {

    const path = this.basePath + '/posts/:postId/poll';

    return this.apiClient.get(path, { postId }, {}, {});

  }

  /**
   * 
   * Example: 
   * createPollChoice(postId, pollId, { name: 'Choice name' });
   * 
   **/
  async createPollChoice(postId, pollId, choice) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/choices';

    return this.apiClient.post(path, { postId, pollId }, {}, choice);

  }

  /**
   * 
   * Example: 
   * updatePollChoiceById(postId, pollId, choiceId, { name: 'Choice name' })
   * 
   **/
  async updatePollChoiceById(postId, pollId, choiceId, choice) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/choices/:choiceId';

    return this.apiClient.put(path, { postId, pollId, choiceId }, {}, choice);

  }

  async deleteChoiceById(postId, pollId, choiceId) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/choices/:choiceId';

    return this.apiClient.delete(path, { postId, pollId, choiceId });

  }

  async deleteUnpublishedPollById(postId, pollId) {

    const path = this.basePath + '/posts/:postId/poll/:pollId';

    return this.apiClient.delete(path, { postId, pollId }, {}, {});

  }

  async voteOnPoll(postId, pollId, choiceId) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/choices/:choiceId/votes';

    return this.apiClient.post(path, { postId, pollId, choiceId }, {}, {});

  }

  async deletePollVote(postId, pollId, choiceId) {

    const path = this.basePath + '/posts/:postId/poll/:pollId/choices/:choiceId/votes';

    return this.apiClient.delete(path, { postId, pollId, choiceId }, {}, {});

  }

}

export default Community;